/**
 * @module GeocodingService
 *
 * Shared geocoding utilities using the Google Geocoding API.
 * Extracted from `WeatherInformationRepository.ts` for reuse across
 * user form, device detail, and future scheduling features.
 */

import {
  COLOR_LOCATION_CONFIRMED,
  COLOR_LOCATION_CONFIRMED_DARK,
  COLOR_LOCATION_GOOD,
  COLOR_LOCATION_GOOD_DARK,
  COLOR_LOCATION_POOR,
  COLOR_LOCATION_POOR_DARK,
  COLOR_LOCATION_UNRELIABLE,
  COLOR_LOCATION_UNRELIABLE_DARK,
} from "@/utils/colors";
import { googleGeocode } from "@/data/repository/GoogleGeocodeGateway";

/**
 * How precisely a set of coordinates locates the address it belongs to.
 *
 * The four geocoded values mirror Google's `geometry.location_type` verbatim. Note that
 * the Geocoding API reports a quality *class*, never a numeric accuracy radius: a radius
 * in metres is a property of a device measuring its own position
 * (`navigator.geolocation`), not of an address lookup.
 *
 * Mirrors `GeoLocationQuality` in IRES Cloud (`data/models/GeoLocation.ts`). The two must
 * stay in step, since the values are persisted and compared on both sides.
 */
export enum GeoLocationQuality {
  /** Google resolved the exact building. The best result available. */
  ROOFTOP = "ROOFTOP",
  /** Interpolated between two known points on the street, typically to house-number level. */
  RANGE_INTERPOLATED = "RANGE_INTERPOLATED",
  /** The centre of a street, block or district, not the address itself. */
  GEOMETRIC_CENTER = "GEOMETRIC_CENTER",
  /** A rough match, usually the centre of a locality. */
  APPROXIMATE = "APPROXIMATE",
  /** A person placed the pin by hand on the map. Never overwritten by automatic geocoding. */
  MANUAL = "MANUAL",
}

/**
 * How much a position can be trusted, as one of four bands the UI paints on the pin.
 *
 * Derived from {@link GeoLocationQuality} plus the two signals Google gives us for free:
 * its `partial_match` flag, and whether reverse-geocoding the result returns the address
 * we asked for. Stored alongside the quality rather than replacing it, because the quality
 * is Google's own vocabulary and stays useful in the tooltip.
 *
 * Mirrors `GeoLocationConfidence` in IRES Cloud (`data/models/GeoLocation.ts`).
 */
export enum GeoLocationConfidence {
  /** A person placed or picked this pin. Green. */
  CONFIRMED = "CONFIRMED",
  /** Automatic, house-number precision, nothing contradicts it. Blue. */
  GOOD = "GOOD",
  /** Automatic, but only street or district precision, or something contradicts it. Yellow. */
  POOR = "POOR",
  /** A locality centre or nothing usable at all. Red. */
  UNRELIABLE = "UNRELIABLE",
}

/** Every value {@link GeoLocationConfidence} may take, for runtime validation. */
export const GEO_LOCATION_CONFIDENCE_VALUES: string[] = Object.values(
  GeoLocationConfidence
);

/**
 * A geocoded or hand-placed position.
 *
 * `quality` is optional because documents written before it existed carry only the two
 * coordinates. `confidence` is optional for the same reason, and additionally because
 * IRES Work writes positions without one; {@link getLocationConfidence} derives a band
 * for both cases rather than requiring a backfill before anything renders.
 */
export interface GeoLocation {
  latitude: number;
  longitude: number;
  quality?: GeoLocationQuality;
  confidence?: GeoLocationConfidence;
}

/** Whether these coordinates were placed by a person and must therefore be preserved. */
/** The address fields the location picker is opened with, as its host surfaces hold them. */
export interface PickerAddress {
  street?: string | null;
  houseNumber?: string | null;
  zipCode?: string | null;
  city?: string | null;
  country?: string | null;
  /**
   * Owner countries to fall back on for the lookup filter; see {@link GeocodeAddress}.
   *
   * Deliberately **not** compared by {@link hasPickerAddressChanged}: it belongs to the record's
   * owner, not to the address, so editing a branch's country must not grey out the pin on every
   * project that branch runs.
   */
  countryFallbacks?: (string | null | undefined)[];
}

/**
 * Whether an edit moved the address in a way a lookup would answer differently.
 *
 * Normalized so `undefined`, `null` and `""` are one value and surrounding whitespace does not
 * count: the forms use all three for "not set", and a trailing space is not a different building.
 *
 * The picker uses it to decide where to open. A pin describes the address it was placed for, so once
 * that address is edited the pin marks a different building, and opening on it invites confirming a
 * position for an address nobody entered. Mirrors `hasAddressChanged` in IRES Cloud
 * (`services/geocoding/AddressGeocoding.ts`), which decides the same thing for the position that is
 * actually persisted (decision of 2026-08-18).
 */
export function hasPickerAddressChanged(
  before: PickerAddress,
  after: PickerAddress
): boolean {
  const normalize = (value: string | null | undefined) => (value ?? "").trim();

  return (
    normalize(before.street) !== normalize(after.street)
    || normalize(before.houseNumber) !== normalize(after.houseNumber)
    || normalize(before.zipCode) !== normalize(after.zipCode)
    || normalize(before.city) !== normalize(after.city)
    || normalize(before.country) !== normalize(after.country)
  );
}

/**
 * An address as Google returned it, split into the fields an IRES address form holds.
 *
 * What the map picker hands back to a form, so that placing a pin can fill the address in
 * (ticket 2610025). `country` is ISO 3166-1 alpha-2 (`DE`), never a plate code or a name: it is
 * what the lookup filter needs and the one form that cannot be misread. A part Google did not
 * return is absent, not empty.
 */
export interface PickedAddressParts {
  street?: string;
  houseNumber?: string;
  zipCode?: string;
  city?: string;
  country?: string;
}

/** What the map picker resolves to when the user applies a pin. */
export interface LocationPickerResult {
  position: GeoLocation;
  /**
   * The address at the pin, when the user placed or picked it themselves. Null for a pin that
   * was only confirmed where the typed address put it: Google's rendering of that address is
   * not something the user chose.
   */
  address: PickedAddressParts | null;
}

/**
 * What the map's address may fill into a form, when the user applies a pin they placed.
 *
 * Only an empty address is filled: street, house number, postcode and city all blank. An address
 * that already holds anything is left as typed, because a pin moved away from it can be deliberate
 * (a parking spot beside a site whose address the reports still need), and replacing a typed
 * address from the map is a separate decision. The country is ignored when judging "empty" and
 * filled only when blank itself, since a form can arrive with one already set (the damage address
 * copies the policyholder's).
 *
 * @returns the parts to write, or null when nothing may or can be filled.
 */
export function getPickedAddressFill(
  entered: PickerAddress,
  picked: PickedAddressParts | null | undefined
): PickedAddressParts | null {
  const isBlank = (value: string | null | undefined) => (value ?? "").trim() === "";

  const isEnteredEmpty = isBlank(entered.street)
    && isBlank(entered.houseNumber)
    && isBlank(entered.zipCode)
    && isBlank(entered.city);

  if (!picked || !isEnteredEmpty) {
    return null;
  }

  const fill: PickedAddressParts = {
    ...(!isBlank(picked.street) && { street: picked.street }),
    ...(!isBlank(picked.houseNumber) && { houseNumber: picked.houseNumber }),
    ...(!isBlank(picked.zipCode) && { zipCode: picked.zipCode }),
    ...(!isBlank(picked.city) && { city: picked.city }),
    ...(isBlank(entered.country) && !isBlank(picked.country) && { country: picked.country }),
  };

  return Object.keys(fill).length > 0 ? fill : null;
}

export function isManuallyPlaced(
  coordinates: GeoLocation | null | undefined
): boolean {
  return coordinates?.quality === GeoLocationQuality.MANUAL;
}

/**
 * Icon for the map affordance next to an address.
 *
 * Filled when the address has a position, outline when it has none, so the automatic
 * geocoding's outcome is visible without opening anything. A single fixed icon would look
 * identical whether an address was located or never resolved at all.
 */
export function getLocationIcon(
  coordinates: GeoLocation | null | undefined
): string {
  return coordinates ? "mdi-map-marker" : "mdi-map-marker-outline";
}

/**
 * What the map affordance beside an address currently stands for.
 *
 * A stored position describes the address it was placed for. Once that address is edited the
 * position marks a different building, so an affordance that keeps painting its confidence
 * band is vouching for something nobody entered. `STALE` is that case, and it is the reason
 * this state exists rather than the affordance reading `coordinates` alone.
 */
export enum AddressPositionState {
  /** A lookup is in flight. */
  LOOKING_UP = "LOOKING_UP",
  /** The address was edited since the position was taken, so the position vouches for nothing. */
  STALE = "STALE",
  /** The stored position belongs to the address on screen. */
  POSITIONED = "POSITIONED",
  /** No position at all: never looked up, or looked up without a usable result. */
  UNPOSITIONED = "UNPOSITIONED",
}

/**
 * Which state the affordance is in.
 *
 * `stored` is the address as saved. Without it staleness cannot be detected at all, which is
 * the old behaviour: a host that does not know its saved address (a record being created)
 * has nothing that could have gone stale.
 */
export function getAddressPositionState(args: {
  coordinates: GeoLocation | null | undefined;
  entered: PickerAddress;
  stored?: PickerAddress;
  isLookingUp?: boolean;
}): AddressPositionState {
  if (args.isLookingUp) {
    return AddressPositionState.LOOKING_UP;
  }

  if (!args.coordinates) {
    return AddressPositionState.UNPOSITIONED;
  }

  return args.stored && hasPickerAddressChanged(args.stored, args.entered)
    ? AddressPositionState.STALE
    : AddressPositionState.POSITIONED;
}

/**
 * Icon for a state.
 *
 * `STALE` gets its own glyph rather than reusing the outline pin: an outline pin already
 * means "no position", and a stale one is a different thing — there is a position, it just
 * does not belong to what is on screen yet.
 */
export function getAddressPositionIcon(
  state: AddressPositionState,
  coordinates: GeoLocation | null | undefined
): string {
  return state === AddressPositionState.STALE
    ? "mdi-map-marker-question"
    : getLocationIcon(coordinates);
}

/**
 * Whether the affordance should paint its confidence band rather than a neutral grey.
 *
 * Only a position that belongs to the address on screen has a band worth showing. A stale
 * one is deliberately colourless: a green pin for a building the user has just edited away
 * from is the exact thing this state exists to stop.
 */
export function showsConfidenceColor(state: AddressPositionState): boolean {
  return state !== AddressPositionState.STALE;
}

/**
 * Whether an address is complete enough to spend a lookup on.
 *
 * Street plus house number, which is the bar Hans set: those two alone identify a great many
 * German addresses (`Henriette-Obermüller-Str. 17` resolves to one rooftop nationwide). The
 * postcode and city are not required, because filling them in from the result is the whole
 * point of the backfill.
 *
 * Below this bar the affordance stays clickable so a lookup can still be asked for by hand —
 * an address with no house number is exactly the case a person has to judge.
 */
export function isReadyForAutomaticLookup(address: PickerAddress): boolean {
  const isFilled = (value: string | null | undefined) =>
    (value ?? "").trim().length > 0;

  return isFilled(address.street) && isFilled(address.houseNumber);
}

/**
 * i18n key describing how exactly an address is located, for the map affordance's tooltip.
 *
 * Coordinates with no quality are legacy documents written before the field existed, so
 * they get a neutral "located" wording rather than a precision claim we cannot back up.
 */
export function getLocationQualityKey(
  coordinates: GeoLocation | null | undefined
): string {
  if (!coordinates) {
    return "location.quality.none";
  }

  return coordinates.quality
    ? `location.quality.${coordinates.quality}`
    : "location.quality.unknown";
}

/**
 * Maps a Google `geometry.location_type` string onto {@link GeoLocationQuality}.
 *
 * @returns the matching quality, or `undefined` for a missing or unrecognised value, so
 *   that a future Google addition is stored as "unknown quality" rather than a wrong one.
 */
export function toGeoLocationQuality(
  locationType: string | undefined | null
): GeoLocationQuality | undefined {
  if (!locationType || locationType === GeoLocationQuality.MANUAL) {
    return undefined;
  }

  return Object.values(GeoLocationQuality).includes(
    locationType as GeoLocationQuality
  )
    ? (locationType as GeoLocationQuality)
    : undefined;
}

/**
 * What reverse-geocoding a result said about it.
 *
 * Not persisted: it is an input to the grading, and the graded band is what gets stored.
 *
 * Mirrors `CrossCheckVerdict` in IRES Cloud (`data/models/GeoLocation.ts`).
 */
export enum CrossCheckVerdict {
  /** The point sits on the requested building: street, postcode and house number all agree. */
  EXACT = "EXACT",
  /** The right street in the right postcode, at some other house number along it. */
  MATCH = "MATCH",
  /** A different street or a different postcode. The result is for somewhere else. */
  MISMATCH = "MISMATCH",
  /** The check did not run, or could not answer. Says nothing either way. */
  UNKNOWN = "UNKNOWN",
}

/**
 * The three graded bands, worst first, so a demotion is a step towards index 0.
 *
 * `CONFIRMED` is not part of the ladder: it is not one step better than `GOOD`, it is a
 * separate claim — that the position was actually verified — and it is awarded by the test in
 * {@link isVerified} rather than by climbing to the top of this list.
 */
const CONFIDENCE_ORDER: GeoLocationConfidence[] = [
  GeoLocationConfidence.UNRELIABLE,
  GeoLocationConfidence.POOR,
  GeoLocationConfidence.GOOD,
];

/** The best band an address Google could not pin down may reach. */
const AMBIGUOUS_CEILING = GeoLocationConfidence.POOR;

/**
 * Bands we believe well enough to read address parts off, worst first.
 *
 * Only `GOOD` and `CONFIRMED` qualify. A `POOR` or `UNRELIABLE` result is one we have already
 * said not to trust, and taking its postcode is trusting it anyway: `Gartenweg 999, 73547
 * Lorch` resolves nothing but a locality centre and would still have filled `Lorch` into the
 * form.
 */
const TRUSTED_FOR_BACKFILL: GeoLocationConfidence[] = [
  GeoLocationConfidence.GOOD,
  GeoLocationConfidence.CONFIRMED,
];

/** Ranks a band so two gradings can be compared. Higher is better. */
function confidenceRank(confidence: GeoLocationConfidence): number {
  return confidence === GeoLocationConfidence.CONFIRMED
    ? CONFIDENCE_ORDER.length
    : CONFIDENCE_ORDER.indexOf(confidence);
}

/**
 * How many Places predictions are resolved before we stop asking.
 *
 * Each one costs a billed request. Four, because that is what `Hauptstr. 12, Berlin` needs to
 * see all four districts that have one; beyond that Places is into different streets.
 */
const PLACES_CANDIDATE_LIMIT = 4;

/** What a lookup knew about the address beyond Google's own answer. */
export interface ConfidenceContext {
  /**
   * Google offered several different buildings and one was picked. A guess must not be
   * painted as a verified position however clean the winning entry looks.
   */
  isAmbiguous?: boolean;
  /**
   * The lookup established that exactly one place answers this address.
   *
   * Required for the top band, and it is a stronger claim than "no ambiguity was noticed".
   * Ambiguity can be entirely invisible: `Hauptstr. 12, Berlin` comes back from the Geocoding
   * API as a single clean rooftop in Tempelhof-Schöneberg although Pankow, Lichtenberg and
   * Spandau each have one too, and it reverse-geocodes perfectly because it *is* a real
   * Hauptstraße 12. So not-noticed is not good enough; uniqueness has to have been checked.
   *
   * It is established two ways: a postcode was entered, which pins the address to one postal
   * district; or Places was consulted about this address and gave a single reading.
   */
  isUnique?: boolean;
}

/**
 * Whether a result has actually been verified, rather than merely looking good.
 *
 * This is what green means, and every clause is load-bearing:
 *
 * - **`EXACT`** — reverse-geocoding the point returned the street, postcode and house number
 *   that were asked for. This is Hans's rule of 2026-08-20: "I would expect a GREEN address
 *   always when the revGC gives the same address."
 * - **`ROOFTOP`** — Google claims a specific building. A lower class can still round-trip to
 *   the right house number by landing on the nearest one, which is agreement by luck.
 * - **established uniqueness** and **no ambiguity** — the two ways a wrong building can
 *   round-trip perfectly. See {@link ConfidenceContext}.
 * - **no `partial_match`** — Google saying it answered a different question, which no amount
 *   of self-agreement afterwards makes up for.
 */
function isVerified(
  quality: GeoLocationQuality | undefined,
  partialMatch: boolean,
  verdict: CrossCheckVerdict,
  context: ConfidenceContext
): boolean {
  return (
    verdict === CrossCheckVerdict.EXACT
    && quality === GeoLocationQuality.ROOFTOP
    && !partialMatch
    && !!context.isUnique
    && !context.isAmbiguous
  );
}

/** The band a quality class earns before any contradicting signal is applied. */
function baseConfidence(
  quality: GeoLocationQuality | undefined
): GeoLocationConfidence {
  switch (quality) {
    case GeoLocationQuality.ROOFTOP:
    case GeoLocationQuality.RANGE_INTERPOLATED:
      return GeoLocationConfidence.GOOD;
    case GeoLocationQuality.GEOMETRIC_CENTER:
      return GeoLocationConfidence.POOR;
    case GeoLocationQuality.APPROXIMATE:
      return GeoLocationConfidence.UNRELIABLE;
    default:
      // No quality at all: a legacy document, an IRES Work write, or a `location_type`
      // Google added after this code was written. We cannot vouch for any of them.
      return GeoLocationConfidence.POOR;
  }
}

/**
 * Grades a geocoding result into one of the four display bands.
 *
 * Green is awarded first, and separately: it is the claim that the position was verified,
 * tested by {@link isVerified}, not the top rung of a ladder. A hand-placed pin is green for
 * the same reason — a person looked at it.
 *
 * Everything else is graded. The quality class sets the starting band and the cross-check
 * moves it by one step, at most, in either direction:
 *
 * - **down**, when Google flagged a partial match or the point resolves back to a different
 *   street. One step and no more, so the yellow "automatic, bad quality" band is actually
 *   reachable instead of every doubt landing on red.
 * - **up**, when the point resolves back to the requested building. Google grades a great
 *   many correct German addresses as `GEOMETRIC_CENTER`, which would otherwise paint a
 *   verified building yellow — the very complaint this grading exists to answer.
 *
 * Finally the band is capped when Google could not pin the address down at all: a coin flip
 * between several buildings can be a reasonable guess, and is never better than yellow.
 *
 * The 2026-08-20 change to what green means is deliberate and was decided with Taimoor:
 * `confidence` now answers only "how much do we believe this position". Who placed it, and
 * therefore whether automatic geocoding may overwrite it, is `quality === MANUAL` and
 * {@link isManuallyPlaced}, which are untouched.
 *
 * @param quality - Google's `location_type`, or `MANUAL` for a hand-placed pin.
 * @param partialMatch - Google's `partial_match` flag: it answered, but not with what was
 *   asked for. This is the signal that catches a mistyped postcode resolving to the centre
 *   of a same-named town in another state.
 * @param verdict - what reverse-geocoding the result said about it.
 * @param context - what the lookup knew beyond Google's answer. Defaults to knowing nothing,
 *   which withholds green rather than granting it: a caller that cannot say whether the
 *   address was ambiguous has not verified anything.
 */
export function toGeoLocationConfidence(
  quality: GeoLocationQuality | undefined,
  partialMatch = false,
  verdict: CrossCheckVerdict = CrossCheckVerdict.UNKNOWN,
  context: ConfidenceContext = {}
): GeoLocationConfidence {
  if (quality === GeoLocationQuality.MANUAL) {
    return GeoLocationConfidence.CONFIRMED;
  }

  if (isVerified(quality, partialMatch, verdict, context)) {
    return GeoLocationConfidence.CONFIRMED;
  }

  const base = CONFIDENCE_ORDER.indexOf(baseConfidence(quality));
  const isContradicted = partialMatch || verdict === CrossCheckVerdict.MISMATCH;

  // Demotion is tested first: a partial match is Google telling us it answered a different
  // question, which no amount of self-agreement afterwards makes up for.
  const step = isContradicted
    ? -1
    : verdict === CrossCheckVerdict.EXACT
      ? 1
      : 0;

  const graded = Math.min(
    Math.max(base + step, 0),
    CONFIDENCE_ORDER.length - 1
  );

  const ceiling = context.isAmbiguous
    ? CONFIDENCE_ORDER.indexOf(AMBIGUOUS_CEILING)
    : CONFIDENCE_ORDER.length - 1;

  return CONFIDENCE_ORDER[Math.min(graded, ceiling)];
}

/**
 * The band to paint for a stored position, deriving one when the document predates the
 * field or was written by a client that does not compute it.
 *
 * Absent coordinates are `UNRELIABLE`: "no coordinate available" is the same red as "very
 * bad coordinate", because in both cases the position cannot be used.
 */
export function getLocationConfidence(
  coordinates: GeoLocation | null | undefined
): GeoLocationConfidence {
  if (!coordinates) {
    return GeoLocationConfidence.UNRELIABLE;
  }

  return (
    coordinates.confidence ?? toGeoLocationConfidence(coordinates.quality)
  );
}

/** Light/dark colour pair for each confidence band. */
const CONFIDENCE_COLORS: Record<
  GeoLocationConfidence,
  { light: string; dark: string }
> = {
  [GeoLocationConfidence.CONFIRMED]: {
    light: COLOR_LOCATION_CONFIRMED,
    dark: COLOR_LOCATION_CONFIRMED_DARK,
  },
  [GeoLocationConfidence.GOOD]: {
    light: COLOR_LOCATION_GOOD,
    dark: COLOR_LOCATION_GOOD_DARK,
  },
  [GeoLocationConfidence.POOR]: {
    light: COLOR_LOCATION_POOR,
    dark: COLOR_LOCATION_POOR_DARK,
  },
  [GeoLocationConfidence.UNRELIABLE]: {
    light: COLOR_LOCATION_UNRELIABLE,
    dark: COLOR_LOCATION_UNRELIABLE_DARK,
  },
};

/**
 * The colour pair for a confidence band named directly, for a legend explaining the scheme.
 *
 * Separate from {@link getLocationConfidenceColors} so a caller with a band and no position does
 * not have to invent coordinates to look its colour up.
 */
export function getConfidenceBandColors(
  band: GeoLocationConfidence
): { light: string; dark: string } {
  return CONFIDENCE_COLORS[band];
}

/**
 * The colour pair for a position's confidence band.
 *
 * Returns both modes rather than resolving one, so this module stays free of the Vuetify
 * instance and can be unit-tested on its own. Components pass the pair through `themed()`.
 */
export function getLocationConfidenceColors(
  coordinates: GeoLocation | null | undefined
): { light: string; dark: string } {
  return getConfidenceBandColors(getLocationConfidence(coordinates));
}

/**
 * i18n key wording how much a position can be trusted, for the map affordance's tooltip.
 *
 * Complements {@link getLocationQualityKey}, which names Google's precision class: the two
 * answer different questions ("how precise" versus "how much do we believe it") and the
 * tooltip shows both.
 */
export function getLocationConfidenceKey(
  coordinates: GeoLocation | null | undefined
): string {
  return `location.confidence.${getLocationConfidence(coordinates)}`;
}

/** Close enough to tell one building from its neighbour. */
const ZOOM_BUILDING = 18;

/** A street and the ones either side of it. */
const ZOOM_STREET = 17;

/** A district, so a street-centre hit is placed in its surroundings. */
const ZOOM_DISTRICT = 15;

/** A whole town with its name legible, so a position in the wrong one is obvious. */
const ZOOM_TOWN = 12;

/**
 * Opening map zoom for a position, derived from how precisely it is known.
 *
 * A flat building-level zoom is what made a wrong position unreadable. Measured on
 * IRGZ00043 (2026-09-10): the damage address reads `Marktplatz, Karlsruhe` and the stored
 * position sits in Cuvio, Italy, so the picker opened on empty Italian countryside at zoom 18
 * and rendered as a plain green rectangle with a pin in the middle. Nothing on screen said
 * "wrong country", because at that zoom there is nothing on screen at all.
 *
 * So the zoom follows the claim being made. A rooftop is a building and is shown as one; a
 * locality centre is a town and is shown as a town, where the name under the pin is legible
 * and a position in the wrong place is obvious at a glance.
 *
 * An **absent** quality zooms out furthest along with `APPROXIMATE`, deliberately: it means
 * "geocoded, precision unknown", which is exactly when the position deserves the least trust.
 * `MANUAL` is the opposite — a person put the pin there — so it gets building zoom.
 */
export function getLocationMapZoom(
  coordinates: GeoLocation | null | undefined
): number {
  switch (coordinates?.quality) {
    case GeoLocationQuality.MANUAL:
    case GeoLocationQuality.ROOFTOP:
      return ZOOM_BUILDING;
    case GeoLocationQuality.RANGE_INTERPOLATED:
      return ZOOM_STREET;
    case GeoLocationQuality.GEOMETRIC_CENTER:
      return ZOOM_DISTRICT;
    default:
      // APPROXIMATE, and a position carrying no quality at all.
      return ZOOM_TOWN;
  }
}

/**
 * The i18n keys making up the map affordance's tooltip, in display order.
 *
 * Two keys, because the colour on its own says nothing to a user who cannot tell the shades
 * apart or does not yet know what they mean: the quality names Google's precision class and
 * the confidence names how much we believe it.
 *
 * An address with no position gets the quality key alone — it already reads "not located
 * yet", and appending "no usable position" to that says the same thing twice.
 */
export function getLocationTooltipKeys(
  coordinates: GeoLocation | null | undefined
): string[] {
  const qualityKey = getLocationQualityKey(coordinates);

  return coordinates
    ? [qualityKey, getLocationConfidenceKey(coordinates)]
    : [qualityKey];
}

/**
 * Maps common vehicle registration plate codes and spelled-out country names (both occur
 * in project, branch and user addresses) to the ISO 3166-1 alpha-2 codes the Google
 * Geocoding API expects in `components=country:`.
 *
 * Sending anything else is worse than sending nothing: `components=country:D` — the German
 * plate code — is answered with ZERO_RESULTS rather than being ignored.
 */
const COUNTRY_TO_ISO: Record<string, string> = {
  A: "AT",
  B: "BE",
  D: "DE",
  DK: "DK",
  E: "ES",
  F: "FR",
  I: "IT",
  L: "LU",
  NL: "NL",
  P: "PT",
  PL: "PL",
  S: "SE",
  DEUTSCHLAND: "DE",
  GERMANY: "DE",
  ALLEMAGNE: "DE",
  NIEMCY: "DE",
  "ÖSTERREICH": "AT",
  OESTERREICH: "AT",
  AUSTRIA: "AT",
  SCHWEIZ: "CH",
  SWITZERLAND: "CH",
  SUISSE: "CH",
  FRANKREICH: "FR",
  FRANCE: "FR",
  ITALIEN: "IT",
  ITALY: "IT",
  ITALIA: "IT",
  NIEDERLANDE: "NL",
  NETHERLANDS: "NL",
  BELGIEN: "BE",
  BELGIUM: "BE",
  LUXEMBURG: "LU",
  LUXEMBOURG: "LU",
  POLEN: "PL",
  POLAND: "PL",
  POLSKA: "PL",
  GB: "GB",
  // Not covered by the ISO2 passthrough below: "UK" is a two-letter code Google rejects,
  // because the ISO 3166-1 alpha-2 code for the United Kingdom is GB.
  UK: "GB",
  "UNITED KINGDOM": "GB",
  "GREAT BRITAIN": "GB",
  GROSSBRITANNIEN: "GB",
  "GROSSBRITANNIEN UND NORDIRLAND": "GB",
  ENGLAND: "GB",
  SCOTLAND: "GB",
  SCHOTTLAND: "GB",
  WALES: "GB",
  "NORTHERN IRELAND": "GB",
  DÄNEMARK: "DK",
  DENMARK: "DK",
  SPANIEN: "ES",
  SPAIN: "ES",
  TSCHECHIEN: "CZ",
  "CZECH REPUBLIC": "CZ",
};

/**
 * Normalizes a country code or name to ISO 3166-1 alpha-2.
 *
 * @returns the ISO2 code, or `null` when the input cannot be recognised as one. Callers
 *   must then omit the `components` filter entirely rather than pass the raw value on.
 */
export function normalizeCountryCode(
  code: string | null | undefined
): string | null {
  const upper = (code ?? "").toUpperCase().trim();

  if (!upper) {
    return null;
  }

  const mapped = COUNTRY_TO_ISO[upper];

  if (mapped) {
    return mapped;
  }

  // Anything already in ISO2 shape is passed through; a two-letter code Google does not
  // know is answered with ZERO_RESULTS, which is the correct outcome for a bad country.
  return /^[A-Z]{2}$/.test(upper) ? upper : null;
}

/**
 * Picks the country filter for a lookup, from the address itself and then its owner.
 *
 * Hans, ticket 2609015: *"I do not know if it is possible to set a pre-filter for the country?
 * If possible, we should use this. The relevant country is the country of the company. If GC
 * should find the location in another country, then we expect the country to be added."*
 *
 * So an address that states its own country decides for itself, and only an address that does
 * not names its owner's country instead. The owner is resolved **branch first, company second**
 * (decision with Taimoor, 2026-09-07): a branch is the nearer statement of where the work
 * happens, and a client operating across a border has branches on both sides while its company
 * address can only name one of them.
 *
 * Each candidate must survive {@link normalizeCountryCode} to be used, and an unusable one is
 * **skipped rather than fatal**: production branch records hold `"Deutschalnd"`, `"Ösetrreich"`
 * and `"line"`, and letting a typo suppress the company's perfectly good country would make the
 * chain worse than no chain. Returning `null` means every candidate failed, and the caller must
 * then omit `components=country:` entirely rather than pass a raw value on.
 *
 * @param candidates - ordered from most to least specific: the address's own country, then the
 *   branch's, then the company's. Nulls and blanks are ignored.
 * @returns the first candidate that resolves to ISO 3166-1 alpha-2, or `null` if none do.
 */
export function resolveCountryFilter(
  candidates: (string | null | undefined)[]
): string | null {
  for (const candidate of candidates) {
    const iso = normalizeCountryCode(candidate);

    if (iso) {
      return iso;
    }
  }

  return null;
}

/**
 * Retrieves the ISO2 country code for a given city using the Google Geocoding API.
 *
 * @param city - The name of the city.
 * @returns The ISO2 country code or null if not found or an error occurs.
 */
export async function getCountryCodeByCity(
  city: string
): Promise<string | null> {
  try {
    const response = await googleGeocode({ address: city });
    const result = response.results?.[0];

    if (result) {
      const countryComponent = result.address_components?.find(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (c: any) => c.types?.includes("country")
      );
      return countryComponent?.short_name ?? null;
    } else {
      return null;
    }
  } catch (error) {
    return null;
  }
}

/**
 * Region to bias a lookup towards when no country filter can be built.
 *
 * `region` is a **soft ranking hint, not a filter**: it breaks ties, and an address outside the
 * region still resolves. That is what makes it safe to default, where `components=country:`
 * would not be — a wrong value there turns a real address into `ZERO_RESULTS`.
 *
 * It matters because a great many IRES records leave `Land` empty, and without any hint Google
 * does not know which country to read the address in. Measured 2026-08-20 on Hans's own record
 * (`Wachthaus 2, Lorch`, no country): with no hint it answers the Lorch locality centre,
 * `APPROXIMATE` and `partial_match`; with `region=de` it answers `Wachthaus 2, 73547 Lorch`
 * with no partial match. Same request count, no Places needed.
 *
 * `de` is the business assumption (decision with Taimoor, 2026-08-20): IRES addresses are
 * predominantly German, and because this only biases ranking, Austrian and Swiss addresses are
 * unaffected in practice.
 */
const DEFAULT_REGION_BIAS = "de";

/**
 * Google statuses that mean "the request was fine, this address simply has no match".
 * Everything else is a configuration or quota problem on our side, which must not be
 * reported to the user as "address not found".
 */
const NO_MATCH_STATUSES = ["ZERO_RESULTS"];

/** How the caller should react to a lookup that produced no position. */
export enum GeocodeOutcome {
  /** A position was found. */
  OK = "OK",
  /** The request succeeded and the address genuinely has no match. */
  NO_MATCH = "NO_MATCH",
  /** The request itself failed: network, quota, or a rejected key. */
  FAILED = "FAILED",
}

/** The address fields a lookup is built from. Every part is optional. */
export interface GeocodeAddress {
  street?: string | null;
  houseNumber?: string | null;
  zipCode?: string | null;
  city?: string | null;
  country?: string | null;
  /**
   * Countries to fall back on when this address states none of its own, ordered from most to
   * least specific: the owning branch, then the owning company. See
   * {@link resolveCountryFilter}.
   *
   * Never part of the address *text* — a country in the query string collapses a building hit
   * to a locality centre. It only ever reaches `components=country:`.
   */
  countryFallbacks?: (string | null | undefined)[];
}

/**
 * One selectable address, as the picker's suggestion list holds them.
 *
 * Produced both by Places autocomplete while the user types and by a lookup that came back
 * ambiguous, so the alternatives Google offered can be handed straight to the same list.
 */
export interface AddressSuggestion {
  placeId: string;
  description: string;
}

/**
 * Looks an address up through Places, which finds addresses the Geocoding API cannot.
 *
 * Injected rather than imported, so this module stays free of any transport. The Places **web
 * service sends no CORS header** (measured 2026-08-20: the Geocoding endpoint answers with
 * `access-control-allow-origin: *`, the Places one with nothing at all), so a page cannot call
 * it at all: in IRES Office the implementation goes through a Cloud callable
 * (`data/repository/GeocodingRepository.ts`), and IRES Cloud calls the web service directly.
 *
 * A caller that passes nothing simply does without the fallback, which is what
 * {@link makeGeoEncodeRequest} does: it wants coordinates and discards everything else.
 */
export type PlacesLookup = (
  query: string,
  isoCountry: string | null
) => Promise<AddressSuggestion[]>;

/** A lookup's full outcome, including what the picker needs to explain itself. */
export interface GeocodeResult {
  outcome: GeocodeOutcome;
  coordinates: GeoLocation | null;
  /** Google's own rendering of what it matched, for showing next to the pin. */
  formattedAddress?: string;
  /** What reverse-geocoding the result said about it. */
  crossCheck: CrossCheckVerdict;
  /**
   * Google offered several different buildings for this address and one of them was picked.
   *
   * Surfaced rather than hidden so the picker can say so and let the user choose, and so the
   * band can be capped: a guess must not be painted as a verified position.
   */
  isAmbiguous: boolean;
  /**
   * The lookup established that exactly one place answers this address.
   *
   * A postcode was entered, or Places was consulted and gave a single reading. What green and
   * the backfill both depend on — see {@link ConfidenceContext}.
   */
  isUnique: boolean;
  /**
   * The postcode and city Google returned, when it is safe to fill them in.
   *
   * Lets a caller supply what the user left out: `Henriette-Obermüller-Str. 17` alone resolves
   * to one rooftop nationwide and comes back carrying `76137` and `Karlsruhe`, so asking for
   * them adds nothing. Read off the winning result's components, so they cost no extra request.
   *
   * **Present only when {@link isUnique}.** `Kurt Schumacher Str. 16` returns three rooftops —
   * Schwäbisch Gmünd, Altenbeken and Düren — and filling in the winner's `73529` would be
   * writing one of three guesses into the user's form as though it were the answer.
   */
  resolvedPostalCode?: string;
  resolvedCity?: string;
  /**
   * The chosen place split into form fields. Set only by {@link geocodeByPlaceId}: a place the
   * user picked from the list is the address they meant, so the picker can fill an empty form
   * with it (ticket 2610025).
   */
  addressParts?: PickedAddressParts;
  /**
   * The readings Google offered, when there was more than one. Empty unless
   * {@link isAmbiguous}.
   *
   * Selectable rather than merely printed: each carries its `place_id`, so the picker can put
   * them in its suggestion list and the user resolves the address exactly by choosing one,
   * instead of being told there is a problem and left to retype it.
   */
  alternatives: AddressSuggestion[];
}

/** One candidate from a `results[]` array, reduced to what ranking needs. */
interface Candidate {
  latitude: number;
  longitude: number;
  quality?: GeoLocationQuality;
  partialMatch: boolean;
  formattedAddress?: string;
  /** Postcode and locality, which are what tell two same-named addresses apart. */
  postalCode?: string;
  locality?: string;
  /** Google's own id for this place, so an alternative can be resolved exactly when picked. */
  placeId?: string;
  /** Street and house number as Google names them, for checking a Places answer agrees. */
  route?: string;
  streetNumber?: string;
  /** The result split into form fields, for filling an empty address from the map. */
  addressParts?: PickedAddressParts;
}

/** Precision order of Google's quality classes, best last. */
const QUALITY_RANK: Record<string, number> = {
  [GeoLocationQuality.APPROXIMATE]: 1,
  [GeoLocationQuality.GEOMETRIC_CENTER]: 2,
  [GeoLocationQuality.RANGE_INTERPOLATED]: 3,
  [GeoLocationQuality.ROOFTOP]: 4,
};

/**
 * Ranks a candidate so the best one in a `results[]` array can be picked.
 *
 * `partial_match` is a hard demotion rather than a small penalty: a partial ROOFTOP hit is
 * a precise position for an address nobody asked about, which is worse than a vague
 * position for the right one.
 */
function scoreCandidate(candidate: Candidate): number {
  const rank = candidate.quality ? QUALITY_RANK[candidate.quality] ?? 0 : 0;
  return candidate.partialMatch ? rank - 10 : rank;
}

/** Reads one `address_components` entry by its type. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function addressComponent(result: any, type: string): string | undefined {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return result?.address_components?.find((component: any) =>
    component?.types?.includes(type)
  )?.long_name;
}

/**
 * The town an address sits in, whatever Google calls it there.
 *
 * `locality` is the usual component, but UK and Irish results carry the town as `postal_town`
 * and no `locality` at all: `10 Downing Street, London` has `postal_town: London`, so reading
 * only `locality` drops the city entirely and renders "Downing Street 10, SW1A 2AA". Losing
 * London from a London address is worse than the Bundesland noise this trimming exists to remove.
 *
 * Deliberately stops there. `administrative_area_level_*` would put the county or the Bundesland
 * back, which is the thing being removed.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function localityOf(result: any): string | undefined {
  return (
    addressComponent(result, "locality") ?? addressComponent(result, "postal_town")
  );
}

/**
 * Splits one Google result into the fields of an IRES address form.
 *
 * The country is read from `short_name`, which Google gives as ISO 3166-1 alpha-2 (`long_name`
 * is the localized name), and passed through {@link normalizeCountryCode} so anything else is
 * dropped rather than written into a form.
 *
 * @returns the parts, or undefined when Google returned none of them.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function addressPartsOf(result: any): PickedAddressParts | undefined {
  const countryCode = normalizeCountryCode(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    result?.address_components?.find((component: any) =>
      component?.types?.includes("country")
    )?.short_name
  );

  const parts: PickedAddressParts = {
    street: addressComponent(result, "route"),
    houseNumber: addressComponent(result, "street_number"),
    zipCode: addressComponent(result, "postal_code"),
    city: localityOf(result),
    ...(countryCode && { country: countryCode }),
  };

  const hasAny = Object.values(parts).some((part) => !!part);

  return hasAny ? parts : undefined;
}

/**
 * An address as IRES writes it, built from the parts rather than from Google's own line.
 *
 * Hans, ticket 2609015: *"When GC delivers results, it sometimes delivers more than we want,
 * e.g. the BUNDESLAND. If we can hide this, we should do it."*
 *
 * Google's `formatted_address` is written for a general audience and carries administrative
 * detail nobody here asked about: `Hauptstr. 12, Berlin` comes back as
 * `Hauptstraße 12, Berlin-Bezirk Tempelhof-Schöneberg, Germany`, and Places descriptions append
 * the country on top. An operator comparing that against the address they typed has to read past
 * two pieces of noise to find the one line that matters.
 *
 * So only the four parts an IRES address has are kept — street, house number, postcode, city —
 * and `administrative_area_level_*`, `country`, `sublocality`, `political` and the rest are
 * dropped. The order matches `DEFAULT_ADDRESS_FORMAT_TEMPLATE` ("Straße Hausnummer, PLZ Ort"),
 * which is what every IRES product already renders.
 *
 * @returns the trimmed line, or undefined when Google gave no usable part. Callers fall back on
 *   `formatted_address` rather than showing nothing: a noisy address beats a blank one.
 */
export function formatAddressParts(parts: {
  route?: string;
  streetNumber?: string;
  postalCode?: string;
  locality?: string;
}): string | undefined {
  const street = [parts.route, parts.streetNumber]
    .filter((part) => !!part && part.trim() !== "")
    .join(" ");
  const town = [parts.postalCode, parts.locality]
    .filter((part) => !!part && part.trim() !== "")
    .join(" ");

  const line = [street, town].filter((part) => part !== "").join(", ");

  return line === "" ? undefined : line;
}

/**
 * Trims a Places prediction for the suggestion list: drops the country, keeps everything else.
 *
 * A weaker trim than {@link formatAddressParts}, and deliberately so. **In a list, the district
 * is the disambiguator, not noise.** `Hauptstr. 12, Berlin` returns four predictions that differ
 * only by borough — Tempelhof-Schöneberg, Pankow, Spandau, Lichtenberg — so reducing them to
 * street and city would print the same line four times and leave the user nothing to choose by.
 * That is the opposite of what the list is for.
 *
 * The country is still dropped: every prediction carries the same one, so it distinguishes
 * nothing, and the lookup is already restricted to it.
 *
 * @param description - Places' own `description`, e.g. `Wachthaus 2, Lorch, Deutschland`.
 */
export function trimSuggestionDescription(description: string): string {
  const parts = description.split(",").map((part) => part.trim());

  // Only when there is something left to show: a two-part prediction is already minimal.
  return parts.length > 2 ? parts.slice(0, -1).join(", ") : description;
}

/** Reduces one Google result to a {@link Candidate}, or null when it carries no position. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toCandidate(result: any): Candidate | null {
  const location = result?.geometry?.location;

  if (typeof location?.lat !== "number" || typeof location?.lng !== "number") {
    return null;
  }

  return {
    latitude: location.lat,
    longitude: location.lng,
    quality: toGeoLocationQuality(result?.geometry?.location_type),
    partialMatch: result?.partial_match === true,
    // Built from the components, not from Google's own line, so the Bundesland and the country
    // never reach the screen. Falls back to Google's when no part came back, because a noisy
    // address still beats a blank one. See `formatAddressParts`.
    formattedAddress:
      formatAddressParts({
        route: addressComponent(result, "route"),
        streetNumber: addressComponent(result, "street_number"),
        postalCode: addressComponent(result, "postal_code"),
        locality: localityOf(result),
      }) ?? result?.formatted_address,
    postalCode: addressComponent(result, "postal_code"),
    locality: addressComponent(result, "locality"),
    placeId: result?.place_id,
    route: addressComponent(result, "route"),
    streetNumber: addressComponent(result, "street_number"),
    addressParts: addressPartsOf(result),
  };
}

/**
 * Normalizes a piece of an address so two spellings of the same thing compare equal.
 *
 * Folds diacritics and the German street-name abbreviations, then drops everything that is
 * not a letter or a digit, so `Friedrich-Str. 3 a` and `friedrichstrasse 3a` match.
 */
function normalizeAddressPart(value: string | null | undefined): string {
  return (value ?? "")
    .toLowerCase()
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/str\.?\b/g, "strasse")
    .replace(/[^a-z0-9]/g, "");
}

/**
 * Builds the query strings to try, best-guess first.
 *
 * Two orderings, because neither wins everywhere: `Zum Kupferhammer 6, 42287 Wuppertal`
 * resolves to the house number while `42287 Wuppertal Zum Kupferhammer 6` only reaches the
 * street, and other addresses behave the other way round. The second is only ever sent when
 * the first came back doubtful, so a clean lookup still costs one request.
 *
 * The country is deliberately not part of the string — spelling it out there degrades the
 * match — and is passed through `components` instead.
 */
function buildQueries(address: GeocodeAddress): string[] {
  const streetLine = [address.street, address.houseNumber]
    .filter(Boolean)
    .join(" ");
  const cityLine = [address.zipCode, address.city].filter(Boolean).join(" ");
  const primary = [streetLine, cityLine].filter(Boolean).join(", ");
  const fallback = [address.zipCode, address.city, address.street, address.houseNumber]
    .filter(Boolean)
    .join(" ");

  return primary === fallback ? [primary] : [primary, fallback];
}

/**
 * Runs one Google forward lookup and returns the best candidate it offered.
 *
 * @returns the candidate, or the outcome explaining why there is none.
 */
async function requestCandidates(
  query: string,
  isoCountry: string | null,
  language?: string
): Promise<{ candidates: Candidate[]; candidate: Candidate | null; outcome: GeocodeOutcome }> {
  try {
    const response = await googleGeocode({
      address: query,
      // A recognised country is a hard filter. Without one, bias the ranking instead of
      // leaving Google to guess which country the address is in.
      ...(isoCountry
        ? { components: `country:${isoCountry}` }
        : { region: DEFAULT_REGION_BIAS }),
      ...(language && { language }),
    });

    const status: string | undefined = response?.status;

    if (status && status !== "OK") {
      return {
        candidates: [],
        candidate: null,
        outcome: NO_MATCH_STATUSES.includes(status)
          ? GeocodeOutcome.NO_MATCH
          : GeocodeOutcome.FAILED,
      };
    }

    const candidates: Candidate[] = (response?.results ?? [])
      .map(toCandidate)
      .filter((candidate: Candidate | null): candidate is Candidate => !!candidate);

    if (candidates.length === 0) {
      return { candidates: [], candidate: null, outcome: GeocodeOutcome.NO_MATCH };
    }

    const best = candidates.reduce((winner, candidate) =>
      scoreCandidate(candidate) > scoreCandidate(winner) ? candidate : winner
    );

    return { candidates, candidate: best, outcome: GeocodeOutcome.OK };
  } catch (error) {
    return { candidates: [], candidate: null, outcome: GeocodeOutcome.FAILED };
  }
}

/**
 * Whether Google offered several genuinely different buildings for one address.
 *
 * Google returns more than one `results[]` entry when an address does not pin down a single
 * place: `Hauptstraße 12` with no city is two rooftops (Nehren and Birgel), `Bahnhofstraße 1`
 * is three (Hannover, Neuwied, Gotha). Picking the highest-scoring one and storing it as a
 * confident position is a coin flip presented as an answer, which is what this detects.
 *
 * Judged on postcode and locality, because that is what distinguishes two same-named
 * addresses; two entries describing the same building in different words are not a conflict.
 * Partial matches are excluded first: those are already demoted on their own, and they are
 * frequently near-miss noise rather than a real second reading of the address.
 *
 * Note this cannot see ambiguity *within* one locality. Asked for `Hauptstr. 12, Berlin`,
 * Google returns a single rooftop in Tempelhof-Schöneberg although four Berlin districts have
 * one; only the Places API reveals those. {@link toGeoLocationConfidence} therefore also
 * withholds the top band from any address that carries no postcode.
 */
function hasAmbiguousCandidates(candidates: Candidate[]): boolean {
  const places = new Set(
    candidates
      .filter(candidate => !candidate.partialMatch)
      .map(candidate =>
        `${normalizeAddressPart(candidate.postalCode)}|${normalizeAddressPart(candidate.locality)}`
      )
  );

  return places.size > 1;
}

/**
 * The candidates a user could pick from, as suggestions.
 *
 * Drops any that cannot be resolved again — a candidate with no `place_id` or nothing to show
 * would be an unselectable row — and partial matches, which are not a real second reading of
 * the address.
 */
function toSuggestions(candidates: Candidate[]): AddressSuggestion[] {
  return candidates
    .filter(candidate => !candidate.partialMatch)
    .filter(candidate => !!candidate.placeId && !!candidate.formattedAddress)
    .map(candidate => ({
      placeId: candidate.placeId as string,
      description: candidate.formattedAddress as string,
    }));
}

/**
 * Whether two place names — two streets, or two localities — refer to the same thing.
 *
 * Containment rather than equality, because Google's own `route` routinely drops a leading
 * article that people do type: it names `Zum Kupferhammer` simply `Kupferhammer`. Requiring
 * equality there would report a mismatch for a perfectly correct position. The length floor
 * keeps a stray fragment from matching everything.
 */
function isSamePlaceName(expected: string, actual: string): boolean {
  const MIN_CONTAINMENT_LENGTH = 4;

  if (!expected || !actual) {
    return false;
  }

  if (expected === actual) {
    return true;
  }

  return (
    Math.min(expected.length, actual.length) >= MIN_CONTAINMENT_LENGTH &&
    (expected.includes(actual) || actual.includes(expected))
  );
}

/**
 * Reverse-geocodes a position and reports what it resolves back to.
 *
 * This is the check that catches a confident-looking result for the wrong place: Google
 * answers a mistyped postcode with the centre of a same-named town in another state, and
 * reverse-geocoding that point returns an address that shares nothing with the input.
 *
 * Street, postcode and **town** are contradictions when they disagree; a house number is not.
 * Anything less precise than a rooftop sits between buildings by definition, so the nearest
 * street address often does not carry the requested number, and treating that as a contradiction
 * would demote every correct interpolated hit. `EXACT` is reserved for a number that was asked
 * for and came back.
 */
async function crossCheckAddress(
  candidate: Candidate,
  address: GeocodeAddress,
  language?: string
): Promise<CrossCheckVerdict> {
  const expectedStreet = normalizeAddressPart(address.street);
  const expectedZip = normalizeAddressPart(address.zipCode);

  if (!expectedStreet && !expectedZip) {
    return CrossCheckVerdict.UNKNOWN;
  }

  try {
    const response = await googleGeocode({
      latlng: `${candidate.latitude},${candidate.longitude}`,
      // Google's own parameter name; asking for street addresses only keeps the answer
      // to the level the comparison is about.
      // eslint-disable-next-line @typescript-eslint/camelcase
      result_type: "street_address",
      ...(language && { language }),
    });

    const result = response?.results?.[0];

    if (response?.status !== "OK" || !result) {
      return CrossCheckVerdict.UNKNOWN;
    }

    const actualStreet = normalizeAddressPart(addressComponent(result, "route"));
    const actualZip = normalizeAddressPart(addressComponent(result, "postal_code"));

    if (expectedStreet && !isSamePlaceName(expectedStreet, actualStreet)) {
      return CrossCheckVerdict.MISMATCH;
    }

    if (expectedZip && actualZip !== expectedZip) {
      return CrossCheckVerdict.MISMATCH;
    }

    // The town has to be compared too, or a rooftop in the wrong town passes as verified.
    // Measured 2026-09-10: `Marktplatz 7, Karlsruhe` with no country filter resolves to
    // `Marktpl. 7, 76646 Bruchsal-Heidelsheim`, whose reverse geocode carries the same street
    // and the same house number, and no postcode was entered to catch it — so this returned
    // `EXACT`, which is what green is built on, for a building 25 km away. It was held at yellow
    // only because Google happened to return a second candidate and the ambiguity cap fired.
    //
    // Containment, not equality, and via `localityOf` so a UK `postal_town` counts: Google
    // renders the same town as "Karlsruhe" and "Karlsruhe-Innenstadt-West". This mirrors the
    // rule Cloud's `agreesWithAddress` already applies to Places candidates.
    const expectedCity = normalizeAddressPart(address.city);
    const actualCity = normalizeAddressPart(localityOf(result));

    // Compared only when Google returned a town, on the same principle as the house number: a
    // component that did not come back is not a contradiction. A point in open country reverse-
    // geocodes to a postcode and nothing else, and that is already graded by its quality class.
    if (expectedCity && actualCity && !isSamePlaceName(expectedCity, actualCity)) {
      return CrossCheckVerdict.MISMATCH;
    }

    const expectedNumber = normalizeAddressPart(address.houseNumber);
    const actualNumber = normalizeAddressPart(
      addressComponent(result, "street_number")
    );

    if (!expectedNumber) {
      // Nothing was claimed about a house number, so the street and postcode agreeing is as
      // far as this can go. It used to report EXACT here, which let an address with no house
      // number earn the same verdict as a confirmed building — and since EXACT is now what
      // green is built on, that would have painted a whole street as a verified address.
      return CrossCheckVerdict.MATCH;
    }

    return actualNumber === expectedNumber
      ? CrossCheckVerdict.EXACT
      : CrossCheckVerdict.MATCH;
  } catch (error) {
    // An unanswerable check says nothing about the position, so it must not move the band.
    return CrossCheckVerdict.UNKNOWN;
  }
}

/**
 * Resolves a Google `place_id` — one the user picked from the suggestion list — to a position.
 *
 * Goes through the Geocoding API rather than Place Details so the answer arrives with a
 * `location_type` we already know how to read, and so the picker stays on a single API.
 * The result is stored as a manual placement, because the user chose this address.
 */
async function requestByPlaceId(
  placeId: string,
  language?: string
): Promise<{ candidate: Candidate | null; status?: string }> {
  try {
    const response = await googleGeocode({
      // Google's own parameter name.
      // eslint-disable-next-line @typescript-eslint/camelcase
      place_id: placeId,
      ...(language && { language }),
    });

    const result = response?.status === "OK"
      ? response.results?.[0]
      : undefined;

    return {
      candidate: result ? toCandidate(result) : null,
      status: response?.status,
    };
  } catch (error) {
    return { candidate: null };
  }
}
/**
 * Whether a Places answer is actually about the address that was asked for.
 *
 * Places ranks by prefix and popularity, not by the whole query, so its top answer is
 * routinely somewhere else entirely. Measured against the live API on 2026-08-20:
 * `Zum Kupferhammer 6, 42287 Wuppertal` returns a `Zum Kupferhammer` in **Frankfurt**, and
 * `Gartenweg 999, 73547 Lorch` (a house number that does not exist) invents `Lorcher Straße`.
 * Taking either would move a correct address to the wrong city, so every part the user
 * actually typed has to come back.
 *
 * Only parts that were entered are checked: the point of the fallback is to answer addresses
 * given without a postcode, so demanding one here would reject exactly those.
 */
function agreesWithAddress(candidate: Candidate, address: GeocodeAddress): boolean {
  const expectedStreet = normalizeAddressPart(address.street);
  const actualStreet = normalizeAddressPart(candidate.route);

  // The street is the one part that must be present and must agree: without it there is
  // nothing tying the answer to the address at all.
  if (!expectedStreet || !isSamePlaceName(expectedStreet, actualStreet)) {
    return false;
  }

  const expectedZip = normalizeAddressPart(address.zipCode);
  if (expectedZip && expectedZip !== normalizeAddressPart(candidate.postalCode)) {
    return false;
  }

  const expectedCity = normalizeAddressPart(address.city);
  if (
    expectedCity
    && !isSamePlaceName(expectedCity, normalizeAddressPart(candidate.locality))
  ) {
    return false;
  }

  const expectedNumber = normalizeAddressPart(address.houseNumber);
  const actualNumber = normalizeAddressPart(candidate.streetNumber);

  // Compared only when Google returned one: a Places answer that stops at the street is a
  // weaker result, not a wrong one, and it is still better than a locality centre.
  return !expectedNumber || !actualNumber || expectedNumber === actualNumber;
}
/**
 * Whether a Geocoding API answer is worth a second opinion from Places.
 *
 * Two quite different reasons, and both matter:
 *
 * - **The answer looks weak.** Anything Google flagged itself, anything resolving back to a
 *   different address, anything it could not pin down, anything that only reached street or
 *   locality precision. Places may simply know the address better.
 * - **The address carries no postcode**, however good the answer looks. This is the only way
 *   to catch ambiguity within one town, which the Geocoding API hides completely: it answers
 *   `Hauptstr. 12, Berlin` with one clean rooftop and never mentions the other three. With a
 *   postcode the address is pinned to one postal district and that cannot arise, so the extra
 *   requests are not spent.
 */
function isDoubtful(
  candidate: Candidate,
  verdict: CrossCheckVerdict,
  isAmbiguous: boolean,
  hasPostcode: boolean
): boolean {
  const band = toGeoLocationConfidence(candidate.quality, candidate.partialMatch, verdict);

  return (
    !hasPostcode
    || candidate.partialMatch
    || verdict === CrossCheckVerdict.MISMATCH
    || isAmbiguous
    || band === GeoLocationConfidence.POOR
    || band === GeoLocationConfidence.UNRELIABLE
  );
}
/**
 * Whether it is worth asking Places for a more precise position than we already have.
 *
 * A forward lookup for a perfectly good address routinely stops at the parcel or street-segment
 * centre while Google's own `place_id` for the same address knows the building. Measured on
 * `Wachthaus 2, 73547 Lorch`: the forward geocode answers `GEOMETRIC_CENTER` and the `place_id`
 * answers `ROOFTOP` **23 m away**, with a byte-identical `formatted_address`. Without this the
 * exact building is reachable only by a person picking from the suggestion list, and an address
 * that could be verified automatically never reaches green.
 *
 * Only with a house number: without one there is no building to find, and a street-centre hit is
 * already the right answer.
 */
function canUpgradeToRooftop(candidate: Candidate, address: GeocodeAddress): boolean {
  return (
    candidate.quality !== GeoLocationQuality.ROOFTOP
    && !!normalizeAddressPart(address.houseNumber)
  );
}

/**
 * Asks Places about the address and reports what it actually knows.
 *
 * One pass answers both of Hans's questions from 2026-08-20, because both have the same root:
 * the Geocoding API is a strict address parser, and Places is not.
 *
 * - **"the initial GC does not find this address"** — `Wachthaus` is not a `route` in Google's
 *   data for Lorch, so the Geocoding API abandons the street and answers with the Lorch
 *   locality centre. Places knows the address; its `place_id` resolves to the rooftop.
 * - **"there are three matches but we show only one proposal"** — asked for `Hauptstr. 12,
 *   Berlin` the Geocoding API returns exactly one clean rooftop, in Tempelhof-Schöneberg,
 *   while Pankow, Lichtenberg and Spandau each have a Hauptstraße 12. Places lists all four.
 *
 * Every prediction is resolved and then checked against the address that was typed, because
 * Places ranks by prefix and popularity rather than by the whole query and its top answer is
 * frequently somewhere else entirely. Measured on the live API: `Zum Kupferhammer 6,
 * Wuppertal` returns five predictions, of which **none** is the Wuppertal address — they are in
 * Frankfurt, Wernigerode, Mülheim and Kassel. Rejecting all five and keeping the Geocoding
 * API's correct Wuppertal answer is the right outcome, and only the per-prediction check gets
 * there; counting predictions would have called that address ambiguous.
 *
 * @returns the best agreeing candidate and whether the agreeing ones disagree about where the
 *   address is. `candidate` is null when nothing Places offered was about this address.
 */
async function consultPlaces(
  address: GeocodeAddress,
  query: string,
  isoCountry: string | null,
  places: PlacesLookup,
  limit: number,
  language?: string
): Promise<{
  candidate: Candidate | null;
  isAmbiguous: boolean;
  alternatives: AddressSuggestion[];
}> {
  const nothing = { candidate: null, isAmbiguous: false, alternatives: [] };

  let suggestions: AddressSuggestion[];

  try {
    suggestions = await places(query, isoCountry);
  } catch (error) {
    // Places is an extra opinion; failing to reach it leaves the original result standing.
    return nothing;
  }

  // `places` is injected, so a caller can hand us anything. Guarded rather than trusted because
  // `geocodeAddress` promises never to throw, and a bad implementation returning no array would
  // otherwise take the whole lookup down instead of merely doing without Places.
  if (!Array.isArray(suggestions)) {
    return nothing;
  }

  const agreeing: Candidate[] = [];

  for (const suggestion of suggestions.slice(0, limit)) {
    const { candidate } = await requestByPlaceId(suggestion.placeId, language);

    if (candidate && agreesWithAddress(candidate, address)) {
      agreeing.push(candidate);
    }
  }

  if (agreeing.length === 0) {
    return nothing;
  }

  const best = agreeing.reduce((winner, candidate) =>
    scoreCandidate(candidate) > scoreCandidate(winner) ? candidate : winner
  );

  const isAmbiguous = hasAmbiguousCandidates(agreeing);

  return {
    candidate: best,
    isAmbiguous,
    alternatives: isAmbiguous ? toSuggestions(agreeing) : [],
  };
}

/**
 * The country assumed when neither the address, its branch nor its company states one.
 *
 * IRES is a German company and its unfiltered addresses are German: of the 74 live branches that
 * hold a real address and resolve no country (measured 2026-09-10), every one names a German town.
 * A guess is always retried without the filter when the answer rejects it, so being wrong here
 * costs requests rather than a wrong position; see {@link geocodeAddress}.
 */
const GUESSED_COUNTRY = "DE";

/** The outcome of trying every query ordering under one country filter. */
interface OrderingAttempt {
  best: Candidate | null;
  /** Candidates from the ordering that produced the winner, for the ambiguity judgement. */
  bestCandidates: Candidate[];
  outcome: GeocodeOutcome;
}

/** Score of a candidate that may be absent, so two attempts can be compared directly. */
function scoreOf(candidate: Candidate | null): number {
  return candidate ? scoreCandidate(candidate) : Number.NEGATIVE_INFINITY;
}

/**
 * Runs the query orderings under one country filter and keeps the best answer.
 *
 * Ambiguity is judged on the ordering that produced the winner. Pooling both orderings would
 * report a conflict whenever the two phrasings merely land in different districts, which is a
 * difference in the question asked, not two readings of one address.
 */
async function runQueryOrderings(
  queries: string[],
  isoCountry: string | null,
  language?: string
): Promise<OrderingAttempt> {
  let best: Candidate | null = null;
  let bestCandidates: Candidate[] = [];
  let outcome = GeocodeOutcome.NO_MATCH;

  for (const query of queries) {
    const attempt = await requestCandidates(query, isoCountry, language);

    if (attempt.outcome === GeocodeOutcome.FAILED) {
      // A rejected or unreachable request says nothing about the address, so trying the
      // other ordering would only burn a second one against the same quota.
      if (!best) {
        outcome = GeocodeOutcome.FAILED;
      }
      break;
    }

    if (!attempt.candidate) {
      continue;
    }

    if (scoreCandidate(attempt.candidate) > scoreOf(best)) {
      best = attempt.candidate;
      bestCandidates = attempt.candidates;
    }
    outcome = GeocodeOutcome.OK;

    // Good enough to stop: a non-partial street-centre hit or better cannot be improved on
    // by re-ordering the same words.
    if (
      scoreCandidate(attempt.candidate) >=
      QUALITY_RANK[GeoLocationQuality.GEOMETRIC_CENTER]
    ) {
      break;
    }
  }

  return { best, bestCandidates, outcome };
}

/**
 * Whether an answer obtained under a *guessed* country filter argues against that guess.
 *
 * Three ways it can, all measured on the live API (2026-09-10):
 *
 * - **`partial_match`** — Google says outright that it answered a different question. A German
 *   filter on `Bahnhofstrasse 1, 8001 Zürich` returns `"Germany"` flagged this way.
 * - **country-level `APPROXIMATE`** — the filter matched nothing inside the country but the
 *   country itself, which is the same case seen from the quality side.
 * - **nothing at all** — `ZERO_RESULTS`, which under a wrong filter is what a short address gets.
 *
 * A `FAILED` attempt is deliberately **not** included: a rejected or unreachable request says
 * nothing about the country, and retrying would spend a second request to learn the same nothing.
 */
function isRejectedByGuessedCountry(attempt: OrderingAttempt): boolean {
  if (attempt.outcome === GeocodeOutcome.FAILED) {
    return false;
  }

  if (!attempt.best) {
    return true;
  }

  return (
    attempt.best.partialMatch
    || attempt.best.quality === GeoLocationQuality.APPROXIMATE
  );
}

/**
 * Resolves an address to a position, graded by how much it can be trusted.
 *
 * Sends up to two forward lookups (see {@link buildQueries}) and, unless switched off, one
 * reverse lookup to cross-check the winner. Never throws: a failure is reported as
 * {@link GeocodeOutcome.FAILED} so the caller can tell it apart from an address that simply
 * does not exist, and say so.
 *
 * When no country is stated anywhere, {@link GUESSED_COUNTRY} is used and the whole query set is
 * retried unfiltered if the answer rejects the guess. The retry runs after the query set, not
 * per ordering, so a rejected guess costs up to two extra forward requests and a clean one costs
 * nothing. See {@link isRejectedByGuessedCountry}.
 *
 * @param address - the address parts; the country may be a name, an ISO2 code or a plate code.
 * @param options.language - locale for Google's `formatted_address`, for display only.
 * @param options.crossCheck - set false to skip the reverse lookup, halving the request cost
 *   where only the coordinates are wanted and the confidence is discarded.
 */
export async function geocodeAddress(
  address: GeocodeAddress,
  options: {
    language?: string;
    crossCheck?: boolean;
    /**
     * Places, for the addresses the Geocoding API cannot resolve. Omit to do without it; see
     * {@link PlacesLookup} for why it is injected rather than imported.
     */
    places?: PlacesLookup;
  } = {}
): Promise<GeocodeResult> {
  const { language, crossCheck = true, places } = options;
  const queries = buildQueries(address);

  if (queries.length === 0 || !queries[0]) {
    return {
      outcome: GeocodeOutcome.NO_MATCH,
      coordinates: null,
      crossCheck: CrossCheckVerdict.UNKNOWN,
      isUnique: false,
      isAmbiguous: false,
      alternatives: [],
    };
  }

  const statedCountry = resolveCountryFilter([
    address.country,
    ...(address.countryFallbacks ?? []),
  ]);
  // Nothing states a country for 100 of 348 live branches (74 of them holding a real address),
  // and none of those can borrow one from a sibling branch, so the chain resolves nothing and
  // the filter used to be omitted entirely. Google then discards the town: measured 2026-09-10,
  // `Marktplatz 7, Karlsruhe` answers `Bruchsal` and `Bretten` unfiltered and the correct
  // Karlsruhe building with `components=country:DE`. `region=de` alone does not fix it.
  const isCountryGuessed = statedCountry === null;
  let isoCountry: string | null = statedCountry ?? GUESSED_COUNTRY;

  let attempt = await runQueryOrderings(queries, isoCountry, language);

  // A *guessed* country is a hint, not a statement, so a lookup it narrowed wrongly has to be
  // retried without it. Measured 2026-09-10: `Bahnhofstrasse 1, 8001 Zürich` under
  // `country:DE` answers `"Germany"`, `APPROXIMATE`, `partial_match: true` rather than
  // `ZERO_RESULTS`, so the wrong guess is always visible in the answer and never silently
  // stored. `scoreCandidate` already demotes `partial_match` by 10 ranks, which is why the two
  // attempts can simply be compared: a rejected guess cannot outscore a real answer.
  if (isCountryGuessed && isRejectedByGuessedCountry(attempt)) {
    const unfiltered = await runQueryOrderings(queries, null, language);

    if (scoreOf(unfiltered.best) > scoreOf(attempt.best)) {
      attempt = unfiltered;
      // Places must be consulted on the same terms as the winning attempt, or the upgrade
      // would re-narrow the search to the country that was just rejected.
      isoCountry = null;
    }
  }

  const { bestCandidates, outcome } = attempt;

  if (!attempt.best) {
    return {
      outcome,
      coordinates: null,
      crossCheck: CrossCheckVerdict.UNKNOWN,
      isUnique: false,
      isAmbiguous: false,
      alternatives: [],
    };
  }

  // Mutable: the Places consult below may replace it with a better position.
  let best: Candidate = attempt.best;

  let verdict = crossCheck
    ? await crossCheckAddress(best, address, language)
    : CrossCheckVerdict.UNKNOWN;

  let isAmbiguous = hasAmbiguousCandidates(bestCandidates);
  let alternatives = isAmbiguous ? toSuggestions(bestCandidates) : [];
  const hasPostcode = !!normalizeAddressPart(address.zipCode);

  // A postcode pins the address to one postal district, so it establishes uniqueness on its
  // own. Without one, only Places can, and it may not be available.
  let isUnique = hasPostcode && !isAmbiguous;

  const isWeak = isDoubtful(best, verdict, isAmbiguous, hasPostcode);
  // A sound answer that is merely imprecise. Then only one prediction is resolved, and only a
  // rooftop is accepted: replacing a good street-centre hit with an equally vague Places answer
  // would spend requests to change nothing.
  const wantsRooftop = !isWeak && canUpgradeToRooftop(best, address);

  if (places && (isWeak || wantsRooftop)) {
    const viaPlaces = await consultPlaces(
      address,
      queries[0],
      isoCountry,
      places,
      wantsRooftop ? 1 : PLACES_CANDIDATE_LIMIT,
      language
    );

    const isPreciseEnough = !wantsRooftop
      || viaPlaces.candidate?.quality === GeoLocationQuality.ROOFTOP;

    if (viaPlaces.candidate && isPreciseEnough) {
      // Two separate things come back from Places, and they are adopted on different terms.

      // What it knows about the *address* is always taken: whether the address names one place
      // or several is a fact about the address, not a property of any one position, and it is
      // the only way to learn it at all. It lowers the band by design, so it must not be
      // weighed as though it were a worse answer.
      isAmbiguous = viaPlaces.isAmbiguous;
      alternatives = viaPlaces.alternatives;
      isUnique = !viaPlaces.isAmbiguous;

      const placesVerdict = crossCheck
        ? await crossCheckAddress(viaPlaces.candidate, address, language)
        : CrossCheckVerdict.UNKNOWN;

      // The *position* is only swapped when it is not a step backwards. Judged on the band it
      // would earn, with the address-level context deliberately left out of both sides, and on
      // Google's precision class as the tie-break: two results can land on the same band while
      // one of them is a building and the other a street centre. Asked about
      // `Friedrich-Str. 3 A, 10117 Berlin`, Places answers the street where the Geocoding API
      // had the building, and both grade yellow.
      const placesBand = confidenceRank(
        toGeoLocationConfidence(
          viaPlaces.candidate.quality,
          viaPlaces.candidate.partialMatch,
          placesVerdict
        )
      );
      const incumbentBand = confidenceRank(
        toGeoLocationConfidence(best.quality, best.partialMatch, verdict)
      );

      const isBetterPosition =
        placesBand > incumbentBand
        || (placesBand === incumbentBand
          && scoreCandidate(viaPlaces.candidate) >= scoreCandidate(best));

      if (isBetterPosition) {
        best = viaPlaces.candidate;
        verdict = placesVerdict;
      }
    }
  }

  const confidence = toGeoLocationConfidence(
    best.quality,
    best.partialMatch,
    verdict,
    { isAmbiguous, isUnique }
  );

  // Two conditions, and both are needed. Uniqueness stops us picking one of several readings;
  // the band stops us reading parts off a position we have just called unusable, which is what
  // `Gartenweg 999, 73547 Lorch` did — nothing but a locality centre, and it still offered
  // "Lorch" to the form.
  const isBackfillable =
    isUnique && TRUSTED_FOR_BACKFILL.includes(confidence);

  return {
    outcome: GeocodeOutcome.OK,
    coordinates: {
      latitude: best.latitude,
      longitude: best.longitude,
      ...(best.quality && { quality: best.quality }),
      confidence,
    },
    formattedAddress: best.formattedAddress,
    ...(isBackfillable && best.postalCode && { resolvedPostalCode: best.postalCode }),
    ...(isBackfillable && best.locality && { resolvedCity: best.locality }),
    crossCheck: verdict,
    isUnique,
    isAmbiguous,
    alternatives,
  };
}







/**
 * Resolves a Google `place_id` — one the user picked from the suggestion list — to a position.
 *
 * Goes through the Geocoding API rather than Place Details so the answer arrives with a
 * `location_type` we already know how to read, and so the picker stays on a single API.
 * The result is stored as a manual placement, because the user chose this address.
 */
export async function geocodeByPlaceId(
  placeId: string,
  language?: string
): Promise<GeocodeResult> {
  const { candidate, status } = await requestByPlaceId(placeId, language);

  if (!candidate) {
    return {
      // No status at all means the request itself never completed.
      outcome:
        !status || !NO_MATCH_STATUSES.includes(status)
          ? GeocodeOutcome.FAILED
          : GeocodeOutcome.NO_MATCH,
      coordinates: null,
      crossCheck: CrossCheckVerdict.UNKNOWN,
      isUnique: false,
      isAmbiguous: false,
      alternatives: [],
    };
  }

  return {
    outcome: GeocodeOutcome.OK,
    coordinates: {
      latitude: candidate.latitude,
      longitude: candidate.longitude,
      quality: GeoLocationQuality.MANUAL,
      confidence: GeoLocationConfidence.CONFIRMED,
    },
    formattedAddress: candidate.formattedAddress,
    addressParts: candidate.addressParts,
    // The user chose this address, so there is nothing left to cross-check.
    crossCheck: CrossCheckVerdict.UNKNOWN,
    // A place id names one specific place, so there is nothing left to be ambiguous about and
    // nothing left to establish: this *is* the one place the user meant.
    isUnique: true,
    isAmbiguous: false,
    alternatives: [],
  };
}

/**
 * Sends a geocoding request to Google Geocoding API to retrieve geographic coordinates
 * based on address components.
 *
 * Kept for callers that want coordinates and nothing else (weather lookup, maps page): it
 * skips the reverse-geocode cross-check, so it costs one request. Use
 * {@link geocodeAddress} where the confidence matters.
 *
 * @param street - The name of the street.
 * @param houseNumber - The house number.
 * @param city - The name of the city.
 * @param zipCode - The ZIP code.
 * @param country - The name, plate code or ISO2 code of the country.
 * @returns The coordinates, carrying Google's quality class, or null if not found.
 */
export async function makeGeoEncodeRequest(
  street: string,
  houseNumber: string,
  city: string,
  zipCode: string,
  country: string,
): Promise<GeoLocation | null> {
  const result = await geocodeAddress(
    { street, houseNumber, city, zipCode, country },
    { crossCheck: false }
  );

  return result.coordinates;
}

/** What sits under a pin: the line shown beneath it, and the same address split into fields. */
export interface ReverseGeocodeResult {
  /** The address as the picker shows it under the pin. */
  line: string;
  /** The address split into form fields, when Google returned any of them. */
  parts?: PickedAddressParts;
}

/**
 * Resolves the address that sits at a set of coordinates, for confirming a pin on the map.
 *
 * @param latitude - Latitude of the point.
 * @param longitude - Longitude of the point.
 * @param language - locale for Google's `formatted_address`.
 * @returns the address, or null when there is nothing there or the request fails. Callers
 *   treat null as "no address to show" rather than an error, since a pin dropped in a field
 *   is legitimate.
 */
export async function reverseGeocode(
  latitude: number,
  longitude: number,
  language?: string
): Promise<ReverseGeocodeResult | null> {
  try {
    const response = await googleGeocode({
      latlng: `${latitude},${longitude}`,
      ...(language && { language }),
    });

    const result = response?.results?.[0];

    if (!result) {
      return null;
    }

    // Trimmed to the four parts an IRES address has: this is the line under the pin, which the
    // user reads to check they are on the right building, and Google's own rendering buries it
    // in Bundesland and country. See `formatAddressParts`.
    const line = formatAddressParts({
      route: addressComponent(result, "route"),
      streetNumber: addressComponent(result, "street_number"),
      postalCode: addressComponent(result, "postal_code"),
      locality: localityOf(result),
    }) ?? result.formatted_address;

    if (!line) {
      return null;
    }

    const parts = addressPartsOf(result);

    return { line, ...(parts && { parts }) };
  } catch (error) {
    return null;
  }
}
